import { FormEvent, useMemo, useState } from 'react'
import { ArrowRight, Building2, Eye, EyeOff, UserRound } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { OnboardingProgressCard, employeeOnboardingSteps, managerOnboardingSteps } from '../components/OnboardingProgressCard'

type AccountMode = 'organisation' | 'employee'

export function Signup() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const inviteToken = params.get('invite') || ''
  const returnTo = params.get('returnTo') || ''
  const invitedEmail = params.get('email') || ''
  const [mode, setMode] = useState<AccountMode>(params.get('mode') === 'employee' || inviteToken ? 'employee' : 'organisation')
  const [fullName, setFullName] = useState('')
  const [organisationName, setOrganisationName] = useState('')
  const [email, setEmail] = useState(invitedEmail)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const isEmployee = mode === 'employee'
  const steps = useMemo(() => (isEmployee ? employeeOnboardingSteps : managerOnboardingSteps), [isEmployee])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!supabase) {
      setError('Supabase is not configured.')
      return
    }

    setBusy(true)
    setError('')
    setMessage('')

    const employeeLoginParams = new URLSearchParams({ employee: '1', email: email.trim() })
    if (returnTo) employeeLoginParams.set('returnTo', returnTo)
    const redirect = `${window.location.origin}${isEmployee ? `/login?${employeeLoginParams.toString()}` : '/onboarding'}`

    const { data, error: signupError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirect,
        data: {
          full_name: fullName.trim(),
          ...(isEmployee
            ? {
                account_mode: 'employee'
              }
            : {
                organisation_name: organisationName.trim(),
                account_mode: 'organisation'
              })
        }
      }
    })

    if (signupError) {
      setBusy(false)
      setError(signupError.message)
      return
    }

    if (isEmployee && data.user) {
      setBusy(false)

      if (data.session) {
        navigate(returnTo || '/employee', { replace: true })
        return
      }

      const loginParams = new URLSearchParams({ employee: '1', email: email.trim() })
      if (returnTo) loginParams.set('returnTo', returnTo)
      navigate(`/login?${loginParams.toString()}`, { replace: true })
      return
    }

    if (data.session && data.user) {
      setBusy(false)
      navigate('/onboarding', { replace: true })
      return
    }

    setBusy(false)
    setMessage('Account created. Continue from the sign-in page.')
  }

  return <div className="auth-page onboarding-auth-page">
    <div className="auth-brand"><img className="mail-percent-logo app-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent" /></div>

    <div className="auth-stage onboarding-auth-stage">
      <aside className="auth-showcase onboarding-auth-showcase">
        <OnboardingProgressCard
          steps={steps}
          currentStep={1}
          sectionLabel={isEmployee ? 'Employee setup' : 'Manager setup'}
          heading={isEmployee ? '3 steps to join your workspace' : '4 steps to launch your workspace'}
        />
      </aside>

      <main className="auth-card auth-card-wide auth-card-elevated onboarding-signup-card">
        <span className="eyebrow">Create your Mail Percent account</span>
        <h1>{isEmployee ? 'Create your employee account' : 'Create your manager account'}</h1>
        <p>
          {isEmployee
            ? 'Create your employee account with the same work email you entered on the invitation link. After you join the organisation, connect Gmail or Outlook once from your Employee Workspace.'
            : 'Step 1 of 4 creates your manager account. After that, you will confirm the organisation, choose the email workspace model, and configure teams and schedules.'}
        </p>

        <div className="signup-mode-switch signup-mode-switch-modern" role="tablist" aria-label="Choose account type">
          <button type="button" className={mode === 'organisation' ? 'active' : ''} onClick={() => setMode('organisation')}>
            <Building2 size={17}/>
            <div>
              <strong>Organisation / manager</strong>
              <span>Create and manage the workspace</span>
            </div>
          </button>
          <button type="button" className={mode === 'employee' ? 'active' : ''} onClick={() => setMode('employee')}>
            <UserRound size={17}/>
            <div>
              <strong>Employee</strong>
              <span>Join a manager’s workspace</span>
            </div>
          </button>
        </div>

        <form onSubmit={submit} className="auth-form auth-form-spacious">
          <label>Your name<input className="search-input" value={fullName} onChange={e => setFullName(e.target.value)} required autoComplete="name" /></label>

          {!isEmployee && (
            <label>Organisation name<input className="search-input" value={organisationName} onChange={e => setOrganisationName(e.target.value)} required /></label>
          )}

          <label>Work email<input className="search-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" readOnly={Boolean(invitedEmail)} />{invitedEmail && <small className="muted">This email came from your organisation invitation and will be used to join the workspace.</small>}</label>

          <label>Password
            <div className="password-field">
              <input className="search-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
              <button type="button" className="password-toggle" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <Eye size={18}/> : <EyeOff size={18}/>}
              </button>
            </div>
          </label>

          {error && <p className="auth-error">{error}</p>}
          {message && <p className="auth-success">{message}</p>}

          <button className="button primary large" disabled={busy}>
            {busy ? <span className="blue-spinner button-spinner"/> : <><span>{isEmployee ? 'Create employee account' : 'Create manager account'}</span><ArrowRight size={17}/></>}
          </button>
        </form>

        <p className="auth-switch">Already have an account? <Link to={returnTo ? `/login?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(returnTo)}` : '/login'}>Sign in</Link></p>
      </main>
    </div>
  </div>
}
